import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Zap } from 'lucide-react';

export type ShiftFormData = {
  shiftDate: string; shiftType: string; managerInCharge: string; staffCount: string;
  firstBookingTime: string; expectedCovers: string;
  eventsToday: string; eventTimes: string; eventLocations: string; specialMenus: string;
  preOrders: string; vips: string; allergens: string; largeTables: string; dogsExpected: boolean;
  staffArrivals: string; roleAllocations: string; staffChanges: string;
  outdoorArea: boolean; firesRequired: boolean; musicRequired: boolean; tvRequired: boolean;
  specialSetupNotes: string; notes: string;
  weatherDescription: string; weatherAdvice: string[]; weatherTemp: number;
};

export const defaultShiftFormData: ShiftFormData = {
  shiftDate: new Date().toISOString().split('T')[0], shiftType: 'Lunch',
  managerInCharge: '', staffCount: '', firstBookingTime: '12:00', expectedCovers: '',
  eventsToday: '', eventTimes: '', eventLocations: '', specialMenus: '',
  preOrders: '', vips: '', allergens: '', largeTables: '', dogsExpected: false,
  staffArrivals: '', roleAllocations: '', staffChanges: '',
  outdoorArea: false, firesRequired: false, musicRequired: true, tvRequired: false,
  specialSetupNotes: '', notes: '',
  weatherDescription: '', weatherAdvice: [], weatherTemp: 0,
};

export type WeatherInfo = { description: string; temp: number; advice: string[]; icon: string };

type Props = {
  step: number; formData: ShiftFormData;
  onChange: (field: keyof ShiftFormData, value: any) => void;
  weather: WeatherInfo | null; onNext: () => void; onPrev: () => void; onGenerate: () => void;
};

const SHIFT_TYPES = ['Breakfast', 'Lunch', 'Dinner', 'Event', 'Sunday', 'Christmas', 'Special'];

export default function ShiftPrepForm({ step, formData, onChange, weather, onNext, onPrev, onGenerate }: Props) {
  const fld = (key: keyof ShiftFormData) => ({
    value: formData[key] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(key, e.target.value),
  });
  const chk = (key: keyof ShiftFormData) => ({
    checked: formData[key] as boolean,
    onCheckedChange: (v: boolean) => onChange(key, v),
  });

  const steps = ['Shift Basics', 'Bookings & Events', 'Staff & Setup'];

  return (
    <div className="space-y-6">
      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {steps.map((label, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${step > i + 1 ? 'bg-green-500 text-white' : step === i + 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              {step > i + 1 ? '✓' : i + 1}
            </div>
            <span className={`text-xs hidden sm:block ${step === i + 1 ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>{label}</span>
            {i < 2 && <div className="w-6 h-px bg-border mx-1" />}
          </div>
        ))}
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Date</Label><Input className="mt-1" type="date" {...fld('shiftDate')} /></div>
            <div>
              <Label>Shift Type</Label>
              <Select value={formData.shiftType} onValueChange={v => onChange('shiftType', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{SHIFT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Manager in Charge</Label><Input className="mt-1" {...fld('managerInCharge')} placeholder="Your name" /></div>
            <div><Label>Staff on Shift</Label><Input className="mt-1" type="number" {...fld('staffCount')} placeholder="Number of staff" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>First Booking / Open Time</Label><Input className="mt-1" type="time" {...fld('firstBookingTime')} /></div>
            <div><Label>Expected Covers</Label><Input className="mt-1" type="number" {...fld('expectedCovers')} placeholder="e.g. 80" /></div>
          </div>
          {weather && (
            <div className={`p-4 rounded-xl border ${weather.description.toLowerCase().includes('rain') || weather.description.toLowerCase().includes('shower') ? 'bg-blue-500/5 border-blue-500/20' : weather.temp > 18 ? 'bg-amber-500/5 border-amber-500/20' : 'bg-muted/50 border-border'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">{weather.icon}</span>
                <div>
                  <p className="font-medium text-sm">{weather.description}</p>
                  <p className="text-xs text-muted-foreground">{weather.temp}°C today</p>
                </div>
              </div>
              {weather.advice.length > 0 && (
                <ul className="space-y-1">{weather.advice.map((a, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <Zap className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />{a}
                  </li>
                ))}</ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <div className="space-y-4">
          <div><Label>Events Today</Label><Textarea className="mt-1" {...fld('eventsToday')} rows={2} placeholder="Describe any events happening today" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Event Times</Label><Input className="mt-1" {...fld('eventTimes')} placeholder="e.g. 7pm – 11pm" /></div>
            <div><Label>Event Location(s)</Label><Input className="mt-1" {...fld('eventLocations')} placeholder="e.g. Barn, Garden" /></div>
          </div>
          <div><Label>Special Menus</Label><Input className="mt-1" {...fld('specialMenus')} placeholder="e.g. Wedding menu, BBQ" /></div>
          <div><Label>Pre-Orders</Label><Textarea className="mt-1" {...fld('preOrders')} rows={2} placeholder="Any pre-ordered food or drinks" /></div>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-3">
            <Label className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> VIPs / Special Guests</Label>
            <Input className="mt-1.5" {...fld('vips')} placeholder="Name, table, special requirements" />
          </div>
          <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-3">
            <Label className="flex items-center gap-1.5 text-destructive">⚠️ Allergens to Flag</Label>
            <Textarea className="mt-1.5" {...fld('allergens')} rows={2} placeholder="IMPORTANT: note any allergen requirements for today's guests" />
          </div>
          <div><Label>Large Tables / Groups</Label><Input className="mt-1" {...fld('largeTables')} placeholder="e.g. Table 8 – party of 16, birthday" /></div>
          <div className="flex items-center gap-2">
            <Checkbox id="dogs" {...chk('dogsExpected')} />
            <label htmlFor="dogs" className="text-sm cursor-pointer">Dogs / cyclists / tourists expected today</label>
          </div>
        </div>
      )}

      {/* Step 3 */}
      {step === 3 && (
        <div className="space-y-4">
          <div><Label>Staff Arrival Times</Label><Textarea className="mt-1" {...fld('staffArrivals')} rows={3} placeholder="e.g. Kitchen 9am, Bar 10:30am, FOH 11am..." /></div>
          <div><Label>Role Allocations</Label><Textarea className="mt-1" {...fld('roleAllocations')} rows={3} placeholder="e.g. Jane – bar lead, Tom – kitchen, Sarah – floor..." /></div>
          <div><Label>Staff Changes During Shift</Label><Input className="mt-1" {...fld('staffChanges')} placeholder="e.g. Mark takes over bar at 6pm" /></div>
          <div className="grid grid-cols-2 gap-3">
            {([
              { key: 'outdoorArea', label: '🌿 Outdoor area to be used' },
              { key: 'firesRequired', label: '🔥 Fires required' },
              { key: 'musicRequired', label: '🎵 Music required' },
              { key: 'tvRequired', label: '📺 TV / Sky / TNT needed' },
            ] as const).map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2 p-3 border border-border rounded-xl">
                <Checkbox id={key} {...chk(key)} />
                <label htmlFor={key} className="text-sm cursor-pointer">{label}</label>
              </div>
            ))}
          </div>
          <div><Label>Special Setup Notes</Label><Textarea className="mt-1" {...fld('specialSetupNotes')} rows={2} placeholder="Anything else needed for setup" /></div>
          <div><Label>Manager Notes</Label><Textarea className="mt-1" {...fld('notes')} rows={2} placeholder="Any additional notes for the team" /></div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex gap-2 pt-2 border-t border-border">
        {step > 1 && <Button variant="outline" onClick={onPrev}>← Back</Button>}
        <div className="flex-1" />
        {step < 3 ? (
          <Button onClick={onNext}>Next →</Button>
        ) : (
          <Button onClick={onGenerate} className="bg-primary text-primary-foreground">Generate Shift Prep Sheet ✓</Button>
        )}
      </div>
    </div>
  );
}
